// src/components/reader/LessonHeader.jsx
//
// The card that opens every lesson (lesson-3a-layout), whatever the section kind:
// an eyebrow (unit / text group), the section title, a meta row (~N min read, done
// state) and -- the point of the whole thing -- Danielle's intro video.
//
// It replaces the header block each section renderer used to draw for itself, so a
// technique, a vocabulary lesson, a set text and a Lit* section all open the same way.
import React from 'react';
import YouTubeEmbed, { parseYouTubeId } from '../YouTubeEmbed.jsx';
import { readingMinutes } from './lessonMeta.js';
import { IconCheck } from '../icons.jsx';
import './lesson.css';

// `blocks` is the section's content.blocks -- used ONLY to estimate the read time and
// to find the optional intro video. Everything else is passed in explicitly, so this
// component never has to know which pack type it is rendering.
export default function LessonHeader({ eyebrow, title, blocks, done = false, showMinutes = true }) {
    const b = (blocks && typeof blocks === 'object') ? blocks : {};
    const minutes = readingMinutes(b);
    const introVideo = typeof b.introVideo === 'string' ? b.introVideo : '';
    // Gate on a PARSEABLE id, not on a non-empty string: a typo'd URL must leave no
    // empty 16:9 hole where the video would be.
    const introRenderable = !!parseYouTubeId(introVideo);

    return (
        <header className="mb-6">
            <div className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4 md:p-5">
                {eyebrow && (
                    <div className="font-display text-book-muted text-xs font-semibold mb-1.5">{eyebrow}</div>
                )}
                <h2 className="font-display text-book-text text-2xl md:text-[28px] font-bold leading-tight [overflow-wrap:anywhere]">
                    {title}
                </h2>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                    {showMinutes && (
                        <span className="font-display text-book-muted text-xs font-semibold">~{minutes} min read</span>
                    )}
                    {done && (
                        <span className="inline-flex items-center gap-1 font-display text-xs font-semibold text-book-accent">
                            <span className="w-3.5 h-3.5 rounded-full bg-book-accent text-book-pageDark flex items-center justify-center">
                                <IconCheck className="w-2.5 h-2.5" />
                            </span>
                            Done
                        </span>
                    )}
                </div>

                {introRenderable && (
                    <div className="mt-4">
                        <div className="font-display text-book-muted text-xs font-semibold mb-1.5">Danielle &middot; intro</div>
                        {/* Click-to-load: no first-paint cost, and no third-party frame
                            until the student asks for it. */}
                        <YouTubeEmbed url={introVideo} title={(title || 'Lesson') + ' — intro'} />
                    </div>
                )}
            </div>
        </header>
    );
}
